import { useState } from "react";
import { GlassCard } from "./ui/GlassCard";
import { Input } from "./ui/Input";
import { Button } from "./ui/Button";
import { useAuth } from "../lib/auth";
import { useI18n } from "../i18n/useI18n";

/**
 * Global "you must change your password" modal.
 *
 * Mounted once at the App root (see App.tsx), the same pattern as
 * PasswordRecoveryModal. Shows whenever the signed-in user's profile has
 * `must_change_password = true` (set by scripts/create-admin-user.mjs for
 * accounts provisioned with a known temporary password). Unlike
 * PasswordRecoveryModal there is no dismiss/cancel path — the account
 * cannot be used with the temporary password past this point, so the
 * user must set their own password before the modal closes.
 */
export function ForcePasswordChangeModal() {
  const { forcedPasswordChangeActive, completeForcedPasswordChange } = useAuth();
  const { t } = useI18n();
  const c = t.app.login;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!forcedPasswordChangeActive) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError(c.recoveryTooShort);
      return;
    }
    if (password !== confirm) {
      setError(c.recoveryMismatch);
      return;
    }
    setBusy(true);
    try {
      const { error } = await completeForcedPasswordChange(password);
      if (error) setError(error);
      else setSuccess(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/50 p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      {/* data-lenis-prevent — see ChartModal.tsx's comment for why every
          fixed-overlay modal needs this on a Lenis-driven page. */}
      <GlassCard data-lenis-prevent className="w-full max-w-sm p-7">
        {success ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="text-3xl text-gold">✦</span>
            <p className="font-display text-lg text-charcoal">{c.recoverySuccess}</p>
          </div>
        ) : (
          <>
            <p className="eyebrow mb-2">{c.forceChangeEyebrow}</p>
            <h3 className="font-display text-xl text-charcoal">{c.forceChangeTitle}</h3>
            <p className="mt-2 text-sm text-charcoal/60">{c.forceChangeHint}</p>

            <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
              <Input
                id="force-change-password"
                type="password"
                label={c.recoveryNewPassword}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Input
                id="force-change-confirm"
                type="password"
                label={c.recoveryConfirmPassword}
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button type="submit" className="mt-2 w-full" disabled={busy}>
                {busy ? c.recoverySaving : c.recoverySave}
              </Button>
            </form>
          </>
        )}
      </GlassCard>
    </div>
  );
}
